import DelayHelper from 'utils/timers';
import {
	BEHAVIOUR_BREATHING,
	BEHAVIOUR_PHYSICS,
	BEHAVIOUR_REPELL,
	PHYS_BOUNDARY,
	PHYS_RAIN,
	REPELL_VECTOR
} from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';

import type { SpacesContext } from 'app/views/home/webgl/particlesOptimised/departments/spaces/SpacesContext';
import type { DepartmentPhase } from 'app/views/home/webgl/particlesOptimised/departments/phases.types';

/**
 * RainPhase — FORK of SpacesDepartment._setSpacesRain +
 * SpacesParticleProcessor.rain.
 *
 * VISUAL: the whole pool becomes soft dots raining down inside the spaces
 * box under randomised gravity; dots crossing the box floor respawn at the
 * box top with a fresh random x (the physics kernel's raindrop branch), and
 * the mouse pushes them around through a weak VECTOR repell.
 *
 * The arm is split in halves 20ms apart, like the reference (its ungrouped
 * DelayHelper is kept — a scene stop inside the window does not cancel the
 * second half, reference bug-compat). The skipNormalParticleUpdates writes
 * are the object pipeline's one-frame-skip hack — not ported (stage-4
 * convention).
 */
export class RainPhase implements DepartmentPhase {
	public readonly name = 'Rain';

	private _ctx: SpacesContext;

	constructor(ctx: SpacesContext) {
		this._ctx = ctx;
	}

	public init(): void {
		//No one-time setup — the phase works straight off the shared lanes.
	}

	public start(): void {
		const midIndex = Math.round(this._ctx.activeCount / 2);

		for (let n = 0; n < midIndex; n++) {
			this._rainLane(this._ctx.range.start + n);
		}

		DelayHelper.setTimeout(() => {
			for (let n = midIndex; n < this._ctx.activeCount; n++) {
				this._rainLane(this._ctx.range.start + n);
			}
		}, 20);
	}

	public tick(): void {
		//All motion is kernel-side (physics rain + VECTOR repell).
	}

	public hide(): void {
		//The next phase's arm rewrites every lane it uses; pending physics
		//state (velocities, the rain flag) carries over exactly like the
		//reference until then.
	}

	public reset(): void {
		//No persistent phase state.
	}

	/** SpacesParticleProcessor.rain, lane-for-line. */
	private _rainLane(lane: number): void {
		const ctx = this._ctx;
		const core = ctx.core;

		ctx.resetLane(lane);
		ctx.deactivateAll(lane);
		ctx.spacesRepellStart(lane);

		//stopBreathing():
		core.behaviourMask[lane] &= ~BEHAVIOUR_BREATHING;
		//hardSetScale(0.08 + rand * 0.08):
		const baseScale = 0.08 + Math.random() * 0.08;
		core.scale[lane] = baseScale;
		core.tScale[lane] = baseScale;
		core.tScaleSaved[lane] = baseScale;

		core.tAlpha[lane] = 0.8 + Math.random() * 0.2;

		//particlePhysics.active = true + rain(): targetX/Y = 0 (transient —
		//the parallax kernel overwrites them this same tick, kept), vectors
		//zeroed, _rain on, then _raindrop(false)'s gravity roll:
		core.behaviourMask[lane] |= BEHAVIOUR_PHYSICS;
		core.tx[lane] = 0;
		core.ty[lane] = 0;
		core.vx[lane] = 0;
		core.vy[lane] = 0;
		//PHYS_BOUNDARY is the fork's lane form of the reference's
		//_processBoundaryTick (true by default, and rain never runs through
		//leftDrop('screens'), the only false writer) — without it the
		//raindrop respawn branch never fires and the whole pool falls
		//through the box floor in a single x=0 column (stage-8 catch):
		core.physFlags[lane] |= PHYS_RAIN | PHYS_BOUNDARY;
		core.gravity[lane] = 0.00002 + Math.random() * 0.0004;
		core.vx[lane] = 0; //(the _raindrop spaces branch re-zeroes vx)

		//repell OVERRIDE: VECTOR, radius 100, randomisePower(0.00005, 1e-7):
		core.behaviourMask[lane] |= BEHAVIOUR_REPELL;
		core.repellType[lane] = REPELL_VECTOR;
		core.repellRadius[lane] = 100;
		core.repellPower[lane] = 0.00005 + Math.random() * 0.0000001;
	}
}
